/**
 * Shared, pure presentation helpers for shipment tracking UI — used by the
 * JourneyHeader, TrackingTimelineV2 and the generic TrackingMap so that event
 * icons, status tones and journey progress stay consistent across all modes.
 */
import type { LucideIcon } from '@lognai/synapse-design-system-v2/icons';
import {
  CalendarCheck,
  PackageCheck,
  PackagePlus,
  Ship,
  Navigation,
  Repeat,
  Anchor,
  PackageMinus,
  ShieldCheck,
  Container,
  Truck,
  CheckCircle2,
  AlertTriangle,
  PauseCircle,
  Unlock,
  Circle,
} from '@lognai/synapse-design-system-v2/icons';
import type {
  ShipmentStatus,
  ShipmentTrackingEvent,
  TrackingEventType,
} from '../../../types/shipments';
import {
  CANONICAL_MILESTONE_GROUPS,
  CANONICAL_MILESTONES,
} from '../../../types/container-milestones';

/** Tones supported by the design-system StatusPill. */
export type StatusTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';
/** Tones supported by the design-system Progress bar. */
export type ProgressTone = 'primary' | 'success' | 'warning' | 'destructive';

/** Per-event-type icon + pill tone. Exceptions/holds are visually loud. */
export const EVENT_VISUALS: Record<TrackingEventType, { icon: LucideIcon; tone: StatusTone }> = {
  booking_confirmed: { icon: CalendarCheck, tone: 'info' },
  cargo_received:    { icon: PackageCheck, tone: 'neutral' },
  loaded:            { icon: PackagePlus, tone: 'info' },
  departed:          { icon: Ship, tone: 'info' },
  in_transit:        { icon: Navigation, tone: 'info' },
  transshipment:     { icon: Repeat, tone: 'info' },
  arrived:           { icon: Anchor, tone: 'info' },
  unloaded:          { icon: PackageMinus, tone: 'neutral' },
  customs_cleared:   { icon: ShieldCheck, tone: 'success' },
  out_for_pickup:    { icon: Container, tone: 'info' },
  out_for_delivery:  { icon: Truck, tone: 'info' },
  delivered:         { icon: CheckCircle2, tone: 'success' },
  exception:         { icon: AlertTriangle, tone: 'destructive' },
  on_hold:           { icon: PauseCircle, tone: 'warning' },
  released:          { icon: Unlock, tone: 'success' },
  other:             { icon: Circle, tone: 'neutral' },
};

/** Pill tone for a shipment's overall status. */
export function statusTone(status: ShipmentStatus): StatusTone {
  switch (status) {
    case 'delivered':
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'destructive';
    case 'on_hold':
      return 'warning';
    case 'draft':
      return 'neutral';
    default:
      return 'info';
  }
}

/** Progress-bar tone matched to the shipment status. */
export function progressTone(status: ShipmentStatus): ProgressTone {
  switch (status) {
    case 'delivered':
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'destructive';
    case 'on_hold':
      return 'warning';
    default:
      return 'primary';
  }
}

// Journey completion by status ordinal (draft → completed). `cancelled` reads
// as a stalled journey (0); `on_hold` sits mid-journey pending resumption.
const STATUS_PROGRESS: Record<ShipmentStatus, number> = {
  draft: 5,
  booked: 20,
  in_transit: 55,
  arrived: 82,
  delivered: 95,
  completed: 100,
  on_hold: 50,
  cancelled: 0,
};

/** 0–100 journey progress for the header bar. */
export function statusProgress(status: ShipmentStatus): number {
  return STATUS_PROGRESS[status] ?? 0;
}

/**
 * 0–100 journey progress from how far down the canonical milestone ladder the
 * shipment has actually got — the position of the furthest milestone with an
 * `actual`, as a fraction of the milestones that apply to this shipment.
 *
 * The ladder is the right signal for both audiences at once: it's populated
 * automatically from the carrier feed for tracked shipments
 * (`sync-oceanio-milestones.ts`) and by hand for untracked ones, so one
 * calculation covers both without branching on subscription state.
 *
 * Transshipment groups that this shipment never uses are excluded from the
 * denominator — otherwise a direct sailing could never exceed ~40%, since
 * 16 of the 24 canonical keys are transshipment legs. A group counts as "in
 * play" if any of its milestones carries a date, the same rule the ladder
 * itself uses to decide which groups to show.
 *
 * Returns `null` when nothing has been recorded yet, so callers can fall back
 * rather than render a misleading 0%.
 */
export function milestoneProgress(
  milestones: { canonicalKey: string; actual?: string; estimated?: string; planned?: string }[]
): number | null {
  if (milestones.length === 0) return null;

  const withAnyDate = new Set(
    milestones.filter((m) => m.actual || m.estimated || m.planned).map((m) => m.canonicalKey)
  );
  const reached = new Set(milestones.filter((m) => m.actual).map((m) => m.canonicalKey));
  if (reached.size === 0) return null;

  const applicable = CANONICAL_MILESTONE_GROUPS.filter(
    (g) => g.phase !== 'transshipment' || g.milestones.some((m) => withAnyDate.has(m.key))
  ).flatMap((g) => g.milestones.map((m) => m.key));

  // Position of the furthest milestone reached, not a count of how many are
  // done — a skipped step earlier in the journey shouldn't drag the bar back.
  let furthest = -1;
  applicable.forEach((key, i) => {
    if (reached.has(key)) furthest = i;
  });
  if (furthest < 0) return null;

  return Math.round(((furthest + 1) / applicable.length) * 100);
}

/**
 * 0–100 journey progress for the hero bar, combining what the ladder proves
 * with what the shipment's status asserts.
 *
 * The ladder is the finer signal, but it only measures milestones somebody
 * actually recorded — and the closing steps of a journey are exactly the ones
 * most often left blank. An arrived-and-discharged shipment whose gate-out and
 * delivery steps were never keyed in measured 58%, well short of the
 * destination it had already reached.
 *
 * So status acts as a floor rather than only as a fallback: it is authoritative
 * about *state*, while the ladder refines *distance*. Whichever is further along
 * wins, which lets a well-tracked shipment run ahead of a stale status without
 * letting a sparse ladder drag a known-arrived one back.
 *
 * Two statuses are not positions on the journey and so never act as a floor:
 * `cancelled` stops the bar dead, and `on_hold` is a state the cargo is in
 * wherever it happens to be.
 */
export function journeyProgress(
  status: ShipmentStatus,
  milestones: Parameters<typeof milestoneProgress>[0]
): number {
  if (status === 'cancelled') return statusProgress(status);

  const byMilestone = milestoneProgress(milestones);
  if (byMilestone === null) return statusProgress(status);
  if (status === 'on_hold') return byMilestone;

  return Math.max(byMilestone, statusProgress(status));
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Whether a lat/lng pair is a position at all.
 *
 * `Number.isFinite` alone is not enough: the tracking provider emits `0 / 0` as its
 * "unknown" sentinel — a port call with no coordinates arrives as
 * `{ name: null, unlocode: 'LKCMB', latitude: 0, longitude: 0 }` alongside the real
 * one — and zero is perfectly finite. Plotted, it lands on Null Island in the Gulf of
 * Guinea, which on an ocean route looks like a plausible waypoint rather than missing
 * data, so Colombo appeared to have been called at 0°N 0°E.
 *
 * Two entries in the UN/LOCODE `ports` dataset store 0/0 for the same reason (the
 * register has no coordinates for them), so this is not only a provider quirk.
 *
 * Exactly-zero on BOTH axes is the sentinel; a genuine 0.0 on one axis (the equator or
 * the prime meridian) is left alone.
 */
export const isRealCoordinate = (lat: unknown, lng: unknown): boolean =>
  isFiniteNumber(lat) && isFiniteNumber(lng) && !(lat === 0 && lng === 0);

/**
 * Best-effort `[lat, lng]` from a tracking event's provider metadata
 * (tracking stores `latitude`/`longitude` there). Manual events return null.
 */
export function extractEventCoords(event: ShipmentTrackingEvent): [number, number] | null {
  const meta = event.metadata;
  if (!meta) return null;
  const lat = (meta as Record<string, unknown>).latitude;
  const lng = (meta as Record<string, unknown>).longitude;
  if (isRealCoordinate(lat, lng)) return [lat as number, lng as number];
  return null;
}

/** Vessel name from a tracking event's provider metadata, if present. */
export function eventVesselName(event: ShipmentTrackingEvent): string | null {
  const name = event.metadata?.['vesselName'];
  return typeof name === 'string' && name.trim() ? name : null;
}

/**
 * The stretch of the ladder where the box is aboard a ship: loaded at POL
 * through discharged at POD, which spans the POL departure, every
 * transshipment hop and the POD arrival in between.
 *
 * Derived from the ordered `CANONICAL_MILESTONES` rather than a hand-written
 * key list, so a milestone added to the ladder cannot silently fall outside it.
 */
const ON_WATER_FIRST = CANONICAL_MILESTONES.findIndex((m) => m.key === 'pol_loaded');
const ON_WATER_LAST = CANONICAL_MILESTONES.findIndex((m) => m.key === 'pod_discharge');

/**
 * Whether a milestone key falls in that stretch.
 *
 * An event that maps to no milestone answers `false`. That is deliberate: a
 * carrier stamps its booked vessel onto events long before and after the sea
 * leg — the depot release, the gate-out at destination — and naming a ship
 * there would say the box is aboard when it is sitting on a chassis.
 *
 * Slightly generous at a transshipment, where the box is on the quay between
 * `tspN_discharge` and `tspN_loaded`. Left that way on purpose: the vessel
 * named on those rows is still the one the reader needs.
 */
export function isOnWater(canonicalKey: string | null | undefined): boolean {
  if (!canonicalKey) return false;
  const index = CANONICAL_MILESTONES.findIndex((m) => m.key === canonicalKey);
  return index >= ON_WATER_FIRST && index <= ON_WATER_LAST;
}

// ---------------------------------------------------------------------------
// Milestone grouping
// ---------------------------------------------------------------------------

export type EventClassifier = 'ACT' | 'EST' | 'PRE' | 'PLN';

export const CLASSIFIER_LABEL: Record<EventClassifier, string> = {
  ACT: 'Actual',
  EST: 'Estimated',
  PRE: 'Predicted',
  PLN: 'Planned',
};

const CLASSIFIER_PRIORITY: Record<EventClassifier, number> = { ACT: 4, EST: 3, PRE: 2, PLN: 1 };

const classifierOf = (e: ShipmentTrackingEvent): EventClassifier | undefined => {
  const c = e.metadata?.['classifier'];
  return typeof c === 'string' && c in CLASSIFIER_PRIORITY ? (c as EventClassifier) : undefined;
};

const unlocodeOf = (e: ShipmentTrackingEvent): string | undefined => {
  const u = e.metadata?.['unlocode'];
  return typeof u === 'string' && u.trim() ? u : undefined;
};

/** One milestone (arrival, departure, load, …) within a port call. */
export interface PortMilestone {
  eventType: TrackingEventType;
  /** Provider description (e.g. "Departure from Port of loading."). */
  title: string;
  status: 'actual' | 'expected';
  classifier?: EventClassifier;
  /** Definitive time (actual if it happened, else the best estimate). */
  time: string;
  /** Prior estimate time, shown alongside an actual when they differ. */
  estimateTime?: string;
}

/**
 * A single port call — the consolidation of a provider's many events at one
 * location into arrival/departure/etc. milestone rows. Each milestone collapses
 * the TRANSPORT + EQUIPMENT duplicates and the PLANNED→ESTIMATED→PREDICTED→
 * ACTUAL estimates, showing the estimate until it goes actual.
 */
export interface PortCallGroup {
  key: string;
  location?: string;
  country?: string;
  unlocode?: string;
  vessel?: string;
  milestones: PortMilestone[];
  /** Earliest milestone time — used to order ports along the voyage. */
  time: string;
  /** True once any milestone at this port has actually happened. */
  reached: boolean;
}

const timeDesc = (a: ShipmentTrackingEvent, b: ShipmentTrackingEvent) =>
  new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime();
const timeAsc = (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime();

/**
 * Group raw tracking events into one card per port call, each listing its
 * arrival/departure/etc. milestones. Provider events are consolidated by
 * UN/LOCODE; manual events (no classifier) stand alone. Ordered origin →
 * destination (earliest first).
 */
export function groupPortCalls(events: ShipmentTrackingEvent[]): PortCallGroup[] {
  // 1. Drop exact TRANSPORT/EQUIPMENT duplicates (same place, milestone text,
  //    time and classifier map to one physical milestone).
  const seen = new Set<string>();
  const deduped = events.filter((e) => {
    const loc = unlocodeOf(e) ?? e.location ?? '';
    const k = `${loc}|${e.description}|${e.eventDate}|${classifierOf(e) ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  // 2. Group by location (provider events); manual events stand alone.
  const ports = new Map<string, ShipmentTrackingEvent[]>();
  for (const e of deduped) {
    const loc = unlocodeOf(e) ?? e.location;
    const key = classifierOf(e) && loc ? `port::${loc.toUpperCase()}` : `solo::${e.id}`;
    const arr = ports.get(key);
    if (arr) arr.push(e);
    else ports.set(key, [e]);
  }

  const result: PortCallGroup[] = [];
  for (const [key, arr] of ports) {
    // 3. Within a port, sub-group by milestone type and pick a representative.
    const byType = new Map<string, ShipmentTrackingEvent[]>();
    for (const e of arr) {
      const t = byType.get(e.eventType);
      if (t) t.push(e);
      else byType.set(e.eventType, [e]);
    }

    const milestones: PortMilestone[] = [];
    for (const evs of byType.values()) {
      const actual = evs.filter((e) => classifierOf(e) === 'ACT').sort(timeDesc)[0];
      const estimate = evs
        .filter((e) => classifierOf(e) !== 'ACT')
        .sort((a, b) => {
          const pa = CLASSIFIER_PRIORITY[classifierOf(a) ?? 'PLN'] ?? 0;
          const pb = CLASSIFIER_PRIORITY[classifierOf(b) ?? 'PLN'] ?? 0;
          return pb - pa || timeDesc(a, b);
        })[0];
      const rep = actual ?? estimate ?? evs[0];
      milestones.push({
        eventType: rep.eventType,
        title: rep.description,
        status: actual ? 'actual' : 'expected',
        classifier: classifierOf(rep),
        time: rep.eventDate,
        estimateTime:
          actual && estimate && estimate.eventDate !== actual.eventDate ? estimate.eventDate : undefined,
      });
    }
    milestones.sort((a, b) => timeAsc(a.time, b.time));

    const rep0 = arr.find((e) => eventVesselName(e)) ?? arr[0];
    result.push({
      key,
      location: rep0.location,
      country: rep0.country,
      unlocode: unlocodeOf(rep0),
      vessel: eventVesselName(rep0) ?? undefined,
      milestones,
      time: milestones[0]?.time ?? rep0.eventDate,
      reached: milestones.some((m) => m.status === 'actual'),
    });
  }

  return result.sort((a, b) => timeAsc(a.time, b.time));
}
